import type { FunctionComponent, ReactNode, JSX } from "react";
import {
  type CpsButtonProps,
  CpsButtonText,
  CpsFeedbackBubble,
  type CpsFeedbackBubbleFill,
} from "corpus";
import type { SVGIconProps } from "@repo/icons/interface";
import { cn } from "@repo/cn";
import {
  DrawerRoot,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer/drawer";
import { Button } from "@/components/ui/button/button";

export interface MessageDrawerRootProps {
  children: ReactNode;
  icon?: FunctionComponent<SVGIconProps>;
  onClose?: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  variant?: CpsFeedbackBubbleFill;
  dismissible?: boolean;
}

export const MessageDrawerRoot = ({
  children,
  icon,
  onClose,
  open,
  setOpen,
  variant = "primary",
  dismissible = true,
}: MessageDrawerRootProps): JSX.Element => {
  return (
    <DrawerRoot dismissible={dismissible} open={open} setOpen={setOpen} onClose={onClose}>
      <DrawerContent>
        {icon !== undefined && <MessageDrawerIcon variant={variant} icon={icon} />}
        {children}
      </DrawerContent>
    </DrawerRoot>
  );
};

interface MessageDrawerIconProps {
  icon: FunctionComponent<SVGIconProps>;
  variant: CpsFeedbackBubbleFill;
}

const MessageDrawerIcon = ({ icon, variant }: MessageDrawerIconProps): JSX.Element => {
  return (
    <div data-testid="message-drawer-icon-id" className="flex justify-center mt-3">
      <CpsFeedbackBubble fill={variant} Icon={icon} />
    </div>
  );
};

interface MessageDrawerTitleProps {
  children: ReactNode;
}

export const MessageDrawerTitle = ({
  children,
}: MessageDrawerTitleProps): JSX.Element => (
  <DrawerHeader>
    <div className="text-center font-medium">
      <DrawerTitle>{children}</DrawerTitle>
    </div>
  </DrawerHeader>
);

interface MessageDrawerBodyProps {
  children: ReactNode;
  className?: string;
}

export const MessageDrawerBody = ({
  children,
  className,
}: MessageDrawerBodyProps): JSX.Element => (
  <div
    className={cn(
      "text-center text-md font-normal text-neutral-500 max-w-md w-full mx-auto flex flex-col gap-y-1 flex-1 overflow-auto min-h-0",
      className,
    )}
  >
    {children}
  </div>
);

interface MessageDrawerActionsProps {
  children: ReactNode;
}

export const MessageDrawerActions = ({
  children,
}: MessageDrawerActionsProps): JSX.Element => {
  return <div className="py-4 flex flex-col gap-4">{children}</div>;
};

export type ButtonProps = CpsButtonProps;

type MessageDrawerActionButtonProps = ButtonProps & {
  children: string;
  onClick?: () => void;
  secondary?: boolean;
  loading?: boolean;
  "data-testid"?: string;
};

export const MessageDrawerActionButton = ({
  children,
  onClick,
  secondary,
  "data-testid": dataTestId,
  loading = false,
  ...props
}: MessageDrawerActionButtonProps): JSX.Element => {
  if (secondary) {
    return (
      <div className="flex justify-center">
        <CpsButtonText onClick={onClick} disabled={props.disabled}>
          {children}
        </CpsButtonText>
      </div>
    );
  }
  return (
    <Button
      {...props}
      onClick={onClick}
      fullWidth
      loading={loading}
      data-testid={dataTestId}
    >
      {children}
    </Button>
  );
};
